import React, { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react"; // Import the arrow icons
import { ArrowUp, ArrowDown } from "lucide-react";

const Calendar = ({ label, setSelectedStartDate, setSelectedEndDate, onDateChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days = [];
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const handlePrevMonth = () => {
    setSelectedDate(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1)
    );
  };

  const handleNextMonth = () => {
    setSelectedDate(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1)
    );
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setDropdownOpen(false);
    
    // Determine which date setter to call based on calendar type
    if (label === "Start Date" && setSelectedStartDate) {
      setSelectedStartDate(date);
    } else if (label === "End Date" && setSelectedEndDate) {
      setSelectedEndDate(date);
    }

    if (onDateChange) {
      onDateChange(date);
    }
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 6;
  };

  const isCurrentDate = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  return (
    <>
      <label htmlFor="calendar" className="text-xs font-medium">
        {label}
      </label>
      <div className="relative w-full">
        <button
          id="calendarButton"
          className="w-full p-2.5 text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <span>
            {selectedDate.toLocaleDateString("en-US", {
              year: "numeric",
              month: "numeric", // Display month as a number
              day: "numeric",
            })}
          </span>
          <span className="text-gray-500">
            <CalendarIcon className="h-4 w-4" />
          </span>
        </button>

        {dropdownOpen && (
          <div className="absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md p-2">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium max-xs:text-xs">
                {selectedDate.toLocaleDateString("en-US", {
                  month: "long", // Display month as name in the calendar
                  year: "numeric",
                })}
              </div>

              <div className="flex">
                <button
                  onClick={handlePrevMonth}
                  className="p-1 hover:bg-gray-100 rounded"
                  aria-label="Previous month"
                >
                  <ArrowUp className="w-4" />
                </button>

                <button
                  onClick={handleNextMonth}
                  className="p-1 hover:bg-gray-100 rounded"
                  aria-label="Next month"
                >
                  <ArrowDown className="w-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2 font-semibold">
              {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
                <div
                  key={index}
                  className={`text-center text-xs ${
                    index === 0 || index === 6
                      ? "text-red-500"
                      : "text-gray-600"
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {getDaysInMonth(selectedDate).map((date, index) => (
                <div key={date ? date.toISOString() : `empty-${index}`} className="text-center">
                  {date ? (
                    <span
                      className={`text-xs ${
                        isWeekend(date) ? "text-red-500" : "text-gray-900"
                      } cursor-pointer ${
                        isCurrentDate(date) ? "bg-blue-200 text-green-900 py-0.5 px-0.5 rounded-full" : ""
                      }`}
                      onClick={() => handleDateSelect(date)}
                    >
                      {date.getDate()}
                    </span>
                  ) : (
                    <span className="text-sm text-transparent">.</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Calendar;
